import React, { useState, useEffect } from "react";
import { Card, Title, Button, Flex, Text, Divider, TextInput } from "@tremor/react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "@tremor/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import * as userAPI from "../../../services/user";
import { toFriendlyTime } from "../../../services/utils";
import { useAuth } from "../../../services/AuthProvider";

export default function AdminUsers() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const getUsers = async (query = searchQuery) => {
    setLoading(true);
    try {
      const response = await userAPI.getUsers(page, limit, query);
      setUsers(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const impersonateUser = async (user) => {
    try {
      const userPersona = await userAPI.impersonate(user._id);
      login(user, userPersona.token);
      navigate("/devices");
    } catch (error) {
      console.error(error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
    getUsers(searchQuery);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    // Clear search when input is empty
    if (!value) {
      setPage(1);
      getUsers('');
    }
  };

  // Read operation
  useEffect(() => {
    getUsers(searchQuery);
  }, [page, limit]);

  return (
    <div>
      {/* Main content without Nav component since it's already included in the parent Admin page */}
      <Flex justifyContent="between" alignItems="start">
        <div>
          <Title>Users</Title>
          <Text>List of users</Text>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2">
          <TextInput
            icon={MagnifyingGlassIcon}
            placeholder="Search by email or name..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="max-w-sm"
          />
          <Button type="submit" loading={loading}>
            Search
          </Button>
        </form>
      </Flex>
      <Divider />
      <Card className="mt-6">
        {searchQuery && (
          <div className="mb-4">
            <Text>Found {total} user{total !== 1 ? 's' : ''} matching "{searchQuery}"</Text>
          </div>
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>Last login</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
              <TableHeaderCell></TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  <Text>Loading...</Text>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  <Text>
                    {searchQuery 
                      ? `No users found matching "${searchQuery}"`
                      : "No users found"
                    }
                  </Text>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{toFriendlyTime(user.lastLogin)}</TableCell>
                  <TableCell>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => impersonateUser(user)}
                    >
                      Impersonate
                    </Button>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}